import { call } from "@saas/api-client";
import { Alert, Button, Card, ErrorAlert, formatDate, Loading, PageHeader, SelectField, Table, TextField, useAsync, useCommand, usePendingId } from "@saas/ui";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api } from "./api";
import { toInstant, tomorrowAt } from "./time";

export function RoomsPage() {
  const rooms = useAsync(() => call(api.GET("/api/rooms")), []);
  const command = useCommand();
  const bookingId = usePendingId();
  const [roomId, setRoomId] = useState("");
  const [startsAt, setStartsAt] = useState(() => tomorrowAt(9));
  const [endsAt, setEndsAt] = useState(() => tomorrowAt(10));
  const [booked, setBooked] = useState<string>();

  const selectedRoom = roomId || rooms.data?.[0]?.id || "";

  const book = async (event: FormEvent) => {
    event.preventDefault();
    setBooked(undefined);
    const body = { id: bookingId.idFor(selectedRoom, startsAt, endsAt), roomId: selectedRoom, startsAt: toInstant(startsAt), endsAt: toInstant(endsAt) };
    if (await command.run(() => call(api.POST("/api/bookings", { body })))) {
      bookingId.done();
      const name = rooms.data?.find((r) => r.id === selectedRoom)?.name ?? "the room";
      setBooked(`${name}, ${formatDate(body.startsAt)} to ${formatDate(body.endsAt)}`);
    }
  };

  return (
    <>
      <PageHeader title="Rooms" description="Rooms of your organisation that can be booked. Pick one and a time." />
      <Card title="Book a room">
        {rooms.data && rooms.data.length === 0 ? (
          <p className="sb-muted">There are no rooms to book yet. Ask your tenant admin to add some.</p>
        ) : (
          <form className="sb-form" onSubmit={book} noValidate>
            <div className="sb-form-row">
              <SelectField label="Room" value={selectedRoom} onChange={(e) => setRoomId(e.target.value)} error={command.fieldError("roomId")}>
                {rooms.data?.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} (seats {r.capacity})
                  </option>
                ))}
              </SelectField>
              <TextField label="Starts" type="datetime-local" required value={startsAt} error={command.fieldError("startsAt")} onChange={(e) => setStartsAt(e.target.value)} />
              <TextField label="Ends" type="datetime-local" required value={endsAt} error={command.fieldError("endsAt")} onChange={(e) => setEndsAt(e.target.value)} />
              <div className="sb-form-action">
                <Button type="submit" variant="primary" disabled={command.pending || !selectedRoom}>
                  Book this room
                </Button>
              </div>
            </div>
            <ErrorAlert error={command.error && !command.fieldError("endsAt") && !command.fieldError("startsAt") ? command.error : undefined} />
            {booked && (
              <Alert tone="success">
                Booked: {booked}. <Link to="/bookings">See all your bookings</Link>
              </Alert>
            )}
          </form>
        )}
      </Card>
      <Card title="Available rooms">
        <ErrorAlert error={rooms.error} />
        {rooms.loading && !rooms.data ? (
          <Loading />
        ) : (
          <Table caption={`${rooms.data?.length ?? 0} room(s) you can book`} headers={["Name", "Seats"]}>
            {rooms.data?.map((r) => (
              <tr key={r.id} data-testid={`room-${r.name}`}>
                <td>{r.name}</td>
                <td>{r.capacity}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
